import React, { useEffect, useRef } from 'react';
import { CloseIcon } from './Icons';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    description?: string;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
    bodyClassName?: string;
    ariaLabel?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
};

let nextModalId = 0;

const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    description,
    children,
    footer,
    size = 'md',
    className,
    bodyClassName,
    ariaLabel,
}) => {
    const onCloseRef = useRef(onClose);
    const dialogRef = useRef<HTMLDivElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const modalIdRef = useRef<string | null>(null);
    if (!modalIdRef.current) {
        nextModalId += 1;
        modalIdRef.current = `flowium-modal-${nextModalId}`;
    }
    const titleId = `${modalIdRef.current}-title`;
    const descriptionId = `${modalIdRef.current}-description`;
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!isOpen) return;
        previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const focusFrame = window.requestAnimationFrame(() => {
            dialogRef.current?.focus();
        });
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onCloseRef.current();
                return;
            }
            if (e.key !== 'Tab' || !dialogRef.current) return;

            const focusableElements = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector),
            ).filter((element) => element.getClientRects().length > 0);
            if (!focusableElements.length) {
                e.preventDefault();
                dialogRef.current.focus();
                return;
            }

            const first = focusableElements[0];
            const last = focusableElements[focusableElements.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === dialogRef.current)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            window.cancelAnimationFrame(focusFrame);
            document.removeEventListener('keydown', handleKeyDown);
            previouslyFocusedRef.current?.focus();
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/20 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden="true"
            />

            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                aria-describedby={description ? descriptionId : undefined}
                aria-label={!title ? ariaLabel : undefined}
                tabIndex={-1}
                className={`relative z-10 flex max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-3xl border border-secondary bg-surface shadow-2xl ${sizeClasses[size]} ${className || ''}`}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close dialog"
                    className="absolute right-4 top-4 rounded-full p-2 text-content-tertiary transition-colors hover:bg-hover hover:text-content"
                >
                    <CloseIcon padding={0} size={4} />
                </button>
                {(title || description) && (
                    <div className="border-b border-secondary px-6 py-5 pr-14">
                        {title && <h3 id={titleId} className="text-xl font-semibold text-content">{title}</h3>}
                        {description && (
                            <p id={descriptionId} className="mt-1 text-sm text-content-secondary">{description}</p>
                        )}
                    </div>
                )}
                <div className={`overflow-y-auto px-6 py-5 ${bodyClassName || ''}`}>
                    {children}
                </div>
                {footer && (
                    <div className="border-t border-secondary px-6 py-4">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Modal;
